import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";

const multAt = (s, g) => Math.floor(Math.exp(g * Math.max(0, s)) * 100) / 100;

const Sky = ({ mult, status, crashPoint }) => {
  const shown = status === "crashed" ? crashPoint : mult;
  const x = Math.min(92, 8 + Math.log(shown) * 28);
  const y = Math.max(8, 88 - Math.log(shown) * 30);
  const color = status === "crashed" ? "#EF4444" : status === "cashed" ? "#10B981" : "#F59E0B";
  return (
    <div className="grain relative h-72 overflow-hidden rounded-xl border border-[#1E293B] bg-[radial-gradient(ellipse_at_bottom,#1e293b_0%,#0b0e14_70%)] sm:h-96">
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path d={`M 4 96 Q ${(x + 4) / 2} 96 ${x} ${y}`} stroke={color} strokeWidth="0.8" fill="none" />
        <path d={`M 4 96 Q ${(x + 4) / 2} 96 ${x} ${y} L ${x} 96 Z`} fill={color} opacity="0.12" />
      </svg>
      {status !== "crashed" && (
        <Rocket className="absolute h-8 w-8 -translate-x-1/2 -translate-y-1/2 rotate-45 transition-[left,top] duration-100" style={{ left: `${x}%`, top: `${y}%`, color }} />
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div data-testid="crash-multiplier" className="font-odds text-6xl font-bold sm:text-7xl" style={{ color }}>
          {shown.toFixed(2)}x
        </div>
        <div data-testid="crash-status" className="mt-2 font-display text-lg font-black uppercase tracking-wide text-slate-300">
          {status === "crashed" ? "Flew away!" : status === "cashed" ? "Cashed out" : status === "running" ? "Flying…" : "Ready for take-off"}
        </div>
      </div>
    </div>
  );
};

export default function FlyHigh() {
  const qc = useQueryClient();
  const { user, setAuthOpen, refresh } = useAuth();
  const { currency, fmt, fromZar, rate } = useCurrency();
  const [stake, setStake] = useState("");
  const [auto, setAuto] = useState("");
  const [round, setRound] = useState(null);
  const [mult, setMult] = useState(1);
  const startRef = useRef(0);
  const { data: history } = useQuery({
    queryKey: ["crash-history"],
    queryFn: () => api.get("/games/crash").then((r) => r.data),
    enabled: !!user,
  });

  useEffect(() => {
    if (round?.status !== "running") return;
    const anim = setInterval(() => setMult(multAt((Date.now() - startRef.current) / 1000, round.growth)), 50);
    const poll = setInterval(async () => {
      const { data } = await api.get(`/games/crash/${round.id}`);
      if (data.status !== "running") {
        setRound(data);
        refresh();
        qc.invalidateQueries({ queryKey: ["crash-history"] });
        if (data.status === "cashed") toast.success(`Auto cash-out at ${data.cashout.toFixed(2)}x`);
      }
    }, 300);
    return () => { clearInterval(anim); clearInterval(poll); };
  }, [round, refresh, qc]);

  const start = async () => {
    if (!user) return setAuthOpen(true);
    try {
      const { data } = await api.post("/games/crash/start", { stake: parseFloat(stake), currency, auto_cashout: auto ? parseFloat(auto) : null });
      startRef.current = Date.now() - data.elapsed * 1000;
      setMult(1);
      setRound(data);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cashout = async () => {
    try {
      const { data } = await api.post(`/games/crash/${round.id}/cashout`);
      setRound(data);
      refresh();
      qc.invalidateQueries({ queryKey: ["crash-history"] });
      if (data.status === "cashed") toast.success(`Cashed out at ${data.cashout.toFixed(2)}x — ${fmt(data.payout_usd)}`);
      else toast.error(`Too late! Flew away at ${data.crash_point.toFixed(2)}x`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const running = round?.status === "running";
  const quick = rate > 5 ? [...new Set([5, 10, 50, 100].map((z) => Math.max(1, Math.round(fromZar(z)))))] : [1, 2, 5, 10];

  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">Crash game</span>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">Fly High</h1>
        <p className="text-sm text-slate-400">The multiplier climbs until the rocket flies away. Cash out before it does.</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <Sky mult={running ? mult : round?.status === "cashed" ? round.cashout : 1} status={round?.status} crashPoint={round?.crash_point || 1} />
        <div className="space-y-3 rounded-xl border border-[#1E293B] bg-[#121824] p-4">
          <label className="text-xs text-slate-400">Stake ({currency})</label>
          <Input data-testid="crash-stake-input" type="number" value={stake} onChange={(e) => setStake(e.target.value)} disabled={running} className="bg-[#0E131F] font-odds" placeholder="0.00" />
          <div className="grid grid-cols-4 gap-1">
            {quick.map((v) => (
              <button key={v} data-testid={`crash-quick-${v}`} disabled={running} onClick={() => setStake(String(v))} className="rounded bg-[#1A2234] py-1 text-xs font-semibold">{v}</button>
            ))}
          </div>
          <label className="text-xs text-slate-400">Auto cash-out (optional)</label>
          <Input data-testid="crash-auto-input" type="number" step="0.1" min="1.01" value={auto} onChange={(e) => setAuto(e.target.value)} disabled={running} className="bg-[#0E131F] font-odds" placeholder="e.g. 2.00" />
          {running ? (
            <Button data-testid="crash-cashout-button" onClick={cashout} className="h-14 w-full bg-[#F59E0B] font-display text-lg font-black uppercase text-[#0B0E14] hover:bg-[#d97706]">
              Cash out {fmt(round.stake_usd * mult)}
            </Button>
          ) : (
            <Button data-testid="crash-start-button" onClick={start} disabled={user && !(parseFloat(stake) > 0)} className="h-14 w-full bg-[#10B981] font-display text-lg font-black uppercase text-[#0B0E14] hover:bg-[#059669]">
              {user ? "Take off" : "Log in to play"}
            </Button>
          )}
          {round && !running && (
            <p data-testid="crash-result" className={`text-sm font-semibold ${round.status === "cashed" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
              {round.status === "cashed" ? `Won ${fmt(round.payout_usd)} at ${round.cashout.toFixed(2)}x` : `Lost — crashed at ${round.crash_point.toFixed(2)}x`}
            </p>
          )}
        </div>
      </div>
      {history?.length > 0 && (
        <section>
          <h2 className="mb-2 font-display text-lg font-black uppercase">Your flights</h2>
          <div className="flex flex-wrap gap-2">
            {history.filter((h) => h.status !== "running").map((h) => (
              <span key={h.id} className={`rounded-full px-3 py-1 font-odds text-xs font-bold ${h.status === "cashed" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
                {(h.status === "cashed" ? h.cashout : h.crash_point).toFixed(2)}x
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
